import React, { useEffect, useState } from "react";
import { authAxios, API } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  ShoppingBag, Headset, Calendar, Home, Smile, UtensilsCrossed,
  Sparkles, FileText, Workflow, Loader2, Check, AlertTriangle,
} from "lucide-react";

const ICON_MAP = {
  ShoppingBag, Headset, Calendar, Home, Smile, UtensilsCrossed,
};

export default function TemplatesDialog({ open, onOpenChange, onPromptLoaded }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState(null);
  const [applyPrompt, setApplyPrompt] = useState(true);
  const [applyFlow, setApplyFlow] = useState(true);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setPicked(null); setDone(null); setError("");
    setApplyPrompt(true); setApplyFlow(true);
    setLoading(true);
    authAxios.get(`${API}/templates`)
      .then(({ data }) => setTemplates(data || []))
      .catch((e) => setError(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [open]);

  const apply = async () => {
    if (!picked) return;
    setBusy(true); setError("");
    try {
      const { data } = await authAxios.post(`${API}/templates/${picked.id}/apply`, {
        apply_prompt: applyPrompt,
        apply_flow: applyFlow,
      });
      setDone({ ...data, name: picked.name });
      // If we updated the prompt, propagate the new value to BotStudio so the
      // textarea reflects it without a page refresh.
      if (applyPrompt && onPromptLoaded) {
        onPromptLoaded(picked.ai_prompt);
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="templates-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" /> Plantillas listas para usar
          </DialogTitle>
          <DialogDescription>
            Cargá un prompt de IA y un flujo prefabricado para arrancar más rápido. Después podés editarlos como quieras.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Card className="border-red-300 bg-red-50">
            <CardContent className="p-3 flex items-center gap-2 text-sm text-red-700">
              <AlertTriangle className="w-4 h-4" /> {error}
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>
        ) : done ? (
          <Card className="border-emerald-300 bg-emerald-50">
            <CardContent className="p-6 text-center space-y-2">
              <div className="w-12 h-12 rounded-full bg-emerald-200 flex items-center justify-center mx-auto">
                <Check className="w-6 h-6 text-emerald-700" />
              </div>
              <h3 className="font-semibold text-emerald-900">¡Plantilla "{done.name}" cargada!</h3>
              <p className="text-sm text-emerald-800">
                {done.prompt_applied && "✓ Prompt actualizado en Bot Studio. "}
                {done.flow_id && "✓ Flujo creado — andá a Flujos para editarlo."}
              </p>
              <Button onClick={() => onOpenChange(false)} className="mt-3 bg-emerald-600 hover:bg-emerald-700 text-white">
                Cerrar
              </Button>
            </CardContent>
          </Card>
        ) : picked ? (
          <div className="space-y-4">
            <button onClick={() => setPicked(null)} className="text-xs text-slate-500 hover:underline" data-testid="templates-back">
              ← Volver al listado
            </button>
            <Card className="border-2" style={{ borderColor: picked.color }}>
              <CardContent className="p-5 space-y-3">
                <div className="flex items-start gap-3">
                  {(() => { const I = ICON_MAP[picked.icon] || Sparkles; return (
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${picked.color}22` }}>
                      <I className="w-6 h-6" style={{ color: picked.color }} />
                    </div>
                  ); })()}
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">{picked.name}</h3>
                    <p className="text-xs text-slate-500">{picked.description}</p>
                  </div>
                </div>

                <div className="border-t pt-3">
                  <p className="text-xs uppercase font-semibold tracking-wider text-slate-500 mb-1 flex items-center gap-1"><FileText className="w-3 h-3" /> Prompt de IA</p>
                  <pre className="text-xs whitespace-pre-wrap font-mono bg-slate-50 p-3 rounded max-h-40 overflow-y-auto" data-testid={`templates-preview-prompt-${picked.id}`}>
                    {picked.ai_prompt}
                  </pre>
                </div>
                <div className="border-t pt-3">
                  <p className="text-xs uppercase font-semibold tracking-wider text-slate-500 mb-1 flex items-center gap-1"><Workflow className="w-3 h-3" /> Flujo de conversación</p>
                  <p className="text-xs text-slate-600">
                    {picked.flow_step_count} pasos · disparador: <code className="bg-slate-100 px-1 rounded">{picked.trigger_keyword}</code>
                  </p>
                  <p className="text-xs text-slate-500 mt-1">Después podés editarlo en <strong>Flujos</strong> para personalizar cada paso, agregar/quitar opciones, etc.</p>
                </div>
              </CardContent>
            </Card>

            <Card><CardContent className="p-4 space-y-3">
              <p className="text-sm font-medium">¿Qué querés cargar?</p>
              <div className="flex items-center justify-between py-1">
                <div>
                  <p className="text-sm">📝 Cargar prompt de IA</p>
                  <p className="text-xs text-slate-500">Reemplaza el prompt actual en Bot Studio.</p>
                </div>
                <Switch checked={applyPrompt} onCheckedChange={setApplyPrompt} data-testid="templates-toggle-prompt" />
              </div>
              <div className="flex items-center justify-between py-1">
                <div>
                  <p className="text-sm">🔀 Crear flujo</p>
                  <p className="text-xs text-slate-500">Genera un nuevo flujo en Flujos con todos los pasos prefabricados.</p>
                </div>
                <Switch checked={applyFlow} onCheckedChange={setApplyFlow} data-testid="templates-toggle-flow" />
              </div>
              <Button
                onClick={apply}
                disabled={busy || (!applyPrompt && !applyFlow)}
                className="w-full bg-amber-500 hover:bg-amber-600 text-black font-medium"
                data-testid="templates-apply-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                Aplicar plantilla
              </Button>
            </CardContent></Card>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {templates.map((t) => {
              const I = ICON_MAP[t.icon] || Sparkles;
              return (
                <Card
                  key={t.id}
                  onClick={() => setPicked(t)}
                  className="cursor-pointer hover:-translate-y-0.5 hover:shadow-lg transition border-2 border-transparent hover:border-amber-300"
                  data-testid={`templates-card-${t.id}`}
                >
                  <CardContent className="p-4 flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${t.color}22` }}>
                      <I className="w-5 h-5" style={{ color: t.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm" style={{ color: t.color }}>{t.name}</p>
                      <p className="text-xs text-slate-600 mt-0.5 line-clamp-2">{t.description}</p>
                      <p className="text-[10px] text-slate-400 mt-1.5 flex items-center gap-2">
                        <span className="flex items-center gap-1"><FileText className="w-2.5 h-2.5" /> Prompt</span>
                        <span className="flex items-center gap-1"><Workflow className="w-2.5 h-2.5" /> {t.flow_step_count} pasos</span>
                      </p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
